import React from 'react'
import Form from '../Form'
import Logger from '#src/services/logger'
import { DepartmentSetter } from '#src/context/department'
import { NotFound } from '#src/components/Errors'
import { getLatestNode } from '#src/services/path'
import {
  PathFlowComponent,
  PathFlowPageComponent,
  SubmitPathFlowPageComponent,
  PathFlowFragment,
  PathFlowPageFragment
} from '#src/generated/graphql'
import { PathLayouts } from '#src/typings'
import { FullBlockLoader, CircleLoader } from '@deal/components'

export interface PathPageProps {
  flowId: string
  pathLayout?: PathLayouts
  pageSlug?: string
  className?: string
  previousPageSlugs: string[]
  previousPathSlugs: string[]
  onMobileNativePageBack: () => void
  onPageBack: () => void
  onUpdateScrollRef: (scrollRef: HTMLDivElement | Window) => void
  onPageSubmitted: (pathFlow: PathFlowFragment, isNewUserRegistered: boolean) => void
}

const PathPage: React.SFC<PathPageProps> = ({
  flowId,
  pathLayout,
  onPageBack,
  onMobileNativePageBack,
  onPageSubmitted,
  onUpdateScrollRef,
  previousPageSlugs,
  previousPathSlugs,
  pageSlug
}) => (
  <PathFlowComponent variables={{ id: flowId }}>
    {({ loading, data: pathFlowQueryData, error }) => {
      if (error) {
        throw new Error(error.message)
      } else if (loading) {
        return <FullBlockLoader loader={CircleLoader} />
      } else if (
        pathFlowQueryData &&
        pathFlowQueryData.pathFlow &&
        pathFlowQueryData.pathFlow.flow
      ) {
        const pathFlow = pathFlowQueryData.pathFlow.flow

        /**
         * If slug isn't defined, get the latest node
         */
        let actualPageSlug = pageSlug
        if (!actualPageSlug) {
          actualPageSlug = getLatestNode(pathFlow).slug
        }

        /**
         * The submit mutation returns the path flow, fetch the edge if it's
         * already in the pages connection
         */
        const existingPathFlowEdge = pathFlow.pagesConnection.edges.find(
          pageEdge => pageEdge.node.slug === actualPageSlug
        )

        return (
          <PathFlowPageComponent
            variables={{
              pathFlowId: pathFlow.id,
              pathFlowPageSlug: actualPageSlug
            }}
            fetchPolicy="network-only"
            skip={existingPathFlowEdge !== undefined}
          >
            {({ data: pathFlowPageData, loading: isPageLoading }) => {
              let pathFlowPage: PathFlowPageFragment

              if (
                pathFlowPageData &&
                pathFlowPageData.pathFlowPage &&
                pathFlowPageData.pathFlowPage.page
              ) {
                pathFlowPage = pathFlowPageData.pathFlowPage.page
              } else if (existingPathFlowEdge) {
                pathFlowPage = existingPathFlowEdge.node
              } else {
                return null
              }

              /**
               * This has a potential to happen mid-deploy where a user is navgating through a path
               * and the page is missing when they attempt to go to it. I'm logging it for now and
               * will address it in the event it starts happening often. @ktsosno
               */
              if (!pathFlowPage) {
                Logger.captureException('Path attempted to navigate to undefined page', {
                  flowId: pathFlow.id
                })

                return <NotFound />
              }

              return (
                <SubmitPathFlowPageComponent
                  onCompleted={response => {
                    if (response.submitPathFlowPage.flow) {
                      const pathFlow = response.submitPathFlowPage.flow
                      const newUserRegistered = response.submitPathFlowPage.newUserRegistered

                      onPageSubmitted(pathFlow, newUserRegistered)
                    } else {
                      throw new Error('Error submitting path flow page')
                    }
                  }}
                >
                  {(submitPathFlowPage, { loading: isPageSubmitting }) => (
                    <>
                      {pathFlow.category &&
                        pathFlow.category.department && (
                          <DepartmentSetter department={pathFlow.category.department} />
                        )}
                      <Form
                        pathLayout={pathLayout}
                        pathFlow={pathFlow}
                        pathFlowPage={pathFlowPage}
                        isPageSubmitting={isPageSubmitting}
                        isPageLoading={isPageLoading}
                        previousPageSlugs={previousPageSlugs}
                        previousPathSlugs={previousPathSlugs}
                        onUpdateScrollRef={onUpdateScrollRef}
                        onPageBack={onPageBack}
                        onMobileNativePageBack={onMobileNativePageBack}
                        onPageSubmitted={() => {
                          submitPathFlowPage({
                            variables: {
                              input: {
                                pageName: pathFlowPage.slug,
                                pathFlowId: pathFlow.id
                              }
                            }
                          })
                        }}
                      />
                    </>
                  )}
                </SubmitPathFlowPageComponent>
              )
            }}
          </PathFlowPageComponent>
        )
      }

      throw new Error(`Error loading path flow, flow id [${flowId}]`)
    }}
  </PathFlowComponent>
)

export default PathPage
